import type { Slot } from "@medplum/fhirtypes";
import { eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { SlotRow } from "../fhir/types";

const SLOT_COLLECTION = "slot";

export interface UseSlotsOptions {
  /** Filter by `Slot.status` — a single status or a set. Omit for all statuses. */
  status?: Slot["status"] | Slot["status"][];
}

export interface UseSlotsResult {
  slots: SlotRow[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/**
 * Live FHIR `Slot` rows for one or more Schedules, ordered by start.
 *
 * The generic read primitive over the unified `slot` collection (which holds
 * both `busy` and `busy-unavailable` rows). `useAvailabilityBlocks` projects its
 * DTO on top of this; the scheduling rail reads booked intervals directly via
 * `useSlots(id, { status: "busy" })`, and overlays device/room booked time by
 * passing those resources' schedule ids as an array. Returns an empty list (no
 * query) when `scheduleId` is undefined or an empty array.
 */
export function useSlots(scheduleId: string | string[] | undefined, options: UseSlotsOptions = {}): UseSlotsResult {
  const slotCollection = useCollection(SLOT_COLLECTION);
  const { status } = options;
  const statuses = status === undefined ? undefined : Array.isArray(status) ? status : [status];
  // Normalize schedule ids so query construction is stable across callers that
  // provide equivalent arrays in different orders.
  const ids = scheduleId === undefined ? [] : Array.isArray(scheduleId) ? scheduleId : [scheduleId];
  const idsKey = [...ids].sort().join(",");

  const result = useLiveQuery({
    query: (q) => {
      const domainIds = idsKey ? idsKey.split(",") : [];
      if (domainIds.length === 0) {
        return q
          .from({ slot: slotCollection })
          .orderBy(({ slot }) => slot.start, "asc")
          .limit(0)
          .select(({ slot }) => ({ ...slot }));
      }
      let query =
        domainIds.length === 1
          ? q.from({ slot: slotCollection }).where(({ slot }) => eq(slot.scheduleDomainId, domainIds[0]))
          : q.from({ slot: slotCollection }).where(({ slot }) => inArray(slot.scheduleDomainId, domainIds));
      if (statuses) {
        query = query.where(({ slot }) => inArray(slot.status, statuses));
      }
      return query.orderBy(({ slot }) => slot.start, "asc").select(({ slot }) => ({ ...slot }));
    },
  });

  return {
    slots: (result.data as SlotRow[] | undefined) ?? [],
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}
